import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, Col, Empty, Input, List, Row, Segmented, Space, Switch, Tag, Typography, message } from 'antd';
import {
    CheckOutlined,
    CloudDownloadOutlined,
    EditOutlined,
    FlagFilled,
    FlagOutlined,
    InboxOutlined,
    PaperClipOutlined,
    UndoOutlined,
    ReloadOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    useBackfillMailAssignmentsMutation,
    useGetMailSyncStatusQuery,
    useGetMailThreadQuery,
    useGetMailThreadsQuery,
    useMarkAllMailReadMutation,
    useMarkMailThreadReadMutation,
    useRunMailSyncMutation,
    useSetMailArchivedMutation,
    useSetMailFollowUpMutation,
} from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import { MailComposer } from '../components/mail/MailComposer';
import { MailThreadView } from '../components/mail/MailThreadView';
import { IMailThreadSummary } from '../../services/nestjs-base-api/types';

const { Text } = Typography;

type Assignee = 'me' | 'unassigned' | 'follow-up' | 'archived' | 'all';

const ThreadRow: React.FC<{
    thread: IMailThreadSummary;
    selected: boolean;
    onSelect: () => void;
    onToggleFollowUp: () => void;
    onToggleArchived: () => void;
}> = ({ thread, selected, onSelect, onToggleFollowUp, onToggleArchived }) => {
    // CRM-linked threads keep their place in the list but drop to secondary
    // weight: most inbound volume is account correspondence, and giving it equal
    // emphasis would make Mail read as a duplicate of the CRM inbox. Hiding it
    // instead would let an unlinked reply fall between the two places.
    const linked = !!thread.account;

    return (
        <List.Item
            onClick={onSelect}
            style={{
                cursor: 'pointer',
                padding: '10px 12px',
                background: selected ? '#f0f5ff' : undefined,
                // Unread outranks the CRM de-emphasis: a linked thread nobody
                // has read yet still has to be findable at a glance.
                opacity: linked && !selected && !thread.unread ? 0.65 : 1,
            }}
        >
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Space size={8} style={{ width: '100%', justifyContent: 'space-between' }}>
                    {/* Bold means unread and nothing else. It briefly also meant
                        "not a CRM thread", which made read mail look unread. */}
                    <Text strong={thread.unread} style={{ fontSize: 13 }}>
                        {thread.unread && (
                            <Badge status="processing" style={{ marginRight: 6 }} />
                        )}
                        {thread.subject || <FormattedMessage id="mail.noSubject" defaultMessage="(no subject)" />}
                    </Text>
                    <Space size={6}>
                        {thread.hasAttachments && (
                            <PaperClipOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                        )}
                        <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                            {new Date(thread.lastMessageAt).toLocaleDateString()}
                        </Text>
                        {/* Flagging is one click from the list: deciding to come
                            back to something happens while scanning, not after
                            opening it. */}
                        <span
                            role="button"
                            onClick={(e) => { e.stopPropagation(); onToggleFollowUp(); }}
                            style={{ cursor: 'pointer', color: thread.followUp ? '#faad14' : '#bfbfbf' }}
                        >
                            {thread.followUp ? <FlagFilled /> : <FlagOutlined />}
                        </span>
                        {/* Removes the conversation from this inbox only. The
                            message stays on the server and on the CRM card. */}
                        <span
                            role="button"
                            onClick={(e) => { e.stopPropagation(); onToggleArchived(); }}
                            style={{ cursor: 'pointer', color: '#bfbfbf' }}
                        >
                            {thread.archived ? <UndoOutlined /> : <InboxOutlined />}
                        </span>
                    </Space>
                </Space>

                <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                    {thread.participants.join(', ')}
                </Text>

                <Space size={6} wrap>
                    {thread.messageCount > 1 && <Tag>{thread.messageCount}</Tag>}
                    {thread.unassignedInbound > 0 && (
                        <Tag color="orange">
                            <FormattedMessage id="mail.unassigned" defaultMessage="Unassigned" />
                        </Tag>
                    )}
                    {thread.account && (
                        <Tag color={thread.account.organisationId ? 'green' : 'purple'} style={{ opacity: 0.8 }}>
                            {thread.account.organisationName || thread.account.companyName}
                        </Tag>
                    )}
                </Space>
            </Space>
        </List.Item>
    );
};

const AdminMailPage: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const jwtToken = user?.jwtToken as string;

    // ?thread= arrives from a CRM card's "View in Mail" link. Start on All in
    // that case: the conversation may well be assigned to the other admin, and
    // landing on an empty Mine list would look like the link was broken.
    const [searchParams] = useSearchParams();
    const threadParam = searchParams.get('thread');

    const [assignee, setAssignee] = useState<Assignee>(threadParam ? 'all' : 'me');
    const [hideLinked, setHideLinked] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedThreadKey, setSelectedThreadKey] = useState<string | null>(threadParam);
    const [composerOpen, setComposerOpen] = useState(false);
    const [replyAll, setReplyAll] = useState(false);
    const [replying, setReplying] = useState(false);

    useEffect(() => {
        if (threadParam) setSelectedThreadKey(threadParam);
    }, [threadParam]);

    const { data, isFetching, refetch } = useGetMailThreadsQuery(
        { jwtToken, assignee, limit: 100, q: search || undefined },
        { skip: !jwtToken },
    );
    const { data: selectedThread } = useGetMailThreadQuery(
        { jwtToken, threadKey: selectedThreadKey as string },
        { skip: !selectedThreadKey || !jwtToken },
    );
    const [backfill, backfillResult] = useBackfillMailAssignmentsMutation();
    const [markRead] = useMarkMailThreadReadMutation();
    const [setFollowUp] = useSetMailFollowUpMutation();
    const [setArchived] = useSetMailArchivedMutation();

    // The remove icon sits next to the date, so a slightly-off click while
    // opening a thread can remove it. Undo lives in the confirmation rather
    // than a dialog: the common case stays one click, and the mistake costs
    // one more.
    const toggleArchived = async (thread: IMailThreadSummary) => {
        const archived = !thread.archived;
        await setArchived({ jwtToken, threadKey: thread.threadKey, archived });

        if (archived && selectedThreadKey === thread.threadKey) {
            setSelectedThreadKey(null);
        }

        const key = `archive-${thread.threadKey}`;
        message.open({
            key,
            type: 'success',
            duration: 6,
            content: (
                <Space>
                    <span>
                        {intl.formatMessage({ id: archived ? 'mail.removed' : 'mail.restored' })}
                    </span>
                    {/* Formatted imperatively, not with <FormattedMessage>: a
                        static message renders outside the provider tree, so the
                        intl context is not there to read. */}
                    <Button
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        onClick={() => {
                            setArchived({
                                jwtToken,
                                threadKey: thread.threadKey,
                                archived: !archived,
                            });
                            message.destroy(key);
                        }}
                    >
                        {intl.formatMessage({ id: 'mail.undo' })}
                    </Button>
                </Space>
            ),
        });
    };
    const [markAllRead, markAllReadResult] = useMarkAllMailReadMutation();

    // Opening a thread marks it read for this admin only. Fire-and-forget: a
    // failed marker is not worth interrupting reading over.
    const openThread = (threadKey: string) => {
        setSelectedThreadKey(threadKey);
        markRead({ jwtToken, threadKey });
    };
    const { data: syncStatus } = useGetMailSyncStatusQuery({ jwtToken }, { skip: !jwtToken });
    const [runSync, runSyncResult] = useRunMailSyncMutation();

    const syncNow = async () => {
        // A sync talks to an external mailbox and can take a few seconds, so it
        // says so immediately: the button's spinner alone is too quiet to read
        // as "something is happening". The key makes the result replace the
        // in-progress toast rather than stack under it.
        message.loading({ content: intl.formatMessage({ id: 'mail.syncing' }), key: 'mail-sync', duration: 0 });
        try {
            const result = await runSync({ jwtToken }).unwrap();
            // Explicit refetch on top of the tag invalidation: if the background
            // refetch fails (an expired token on a long-open tab, say) the list
            // silently keeps showing pre-sync mail, which reads as "the sync did
            // nothing". This way a stale list surfaces as an error instead.
            await refetch();
            message.success({
                content: intl.formatMessage({ id: 'mail.syncDone' }, { fetched: result.fetched }),
                key: 'mail-sync',
            });
        } catch (error: any) {
            message.error({
                content: error?.data?.message || intl.formatMessage({ id: 'mail.syncFailed' }),
                key: 'mail-sync',
                duration: 8,
            });
        }
    };

    const threads = useMemo(() => {
        const all = data?.threads ?? [];
        return hideLinked ? all.filter((thread) => !thread.account) : all;
    }, [data?.threads, hideLinked]);

    const runBackfill = async () => {
        try {
            // Loops because each call handles one page; a mailbox with thousands
            // of messages needs more than one pass.
            let total = 0;
            let result;
            do {
                result = await backfill({ jwtToken, limit: 500 }).unwrap();
                total += result.assigned;
            } while (result.scanned > 0);
            message.success(intl.formatMessage({ id: 'mail.backfillDone' }, { count: total }));
        } catch {
            message.error(intl.formatMessage({ id: 'mail.backfillFailed' }));
        }
    };

    return (
        <div style={{ padding: 24 }}>
            <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} wrap>
                <Space wrap>
                    <Segmented
                        value={assignee}
                        onChange={(value) => setAssignee(value as Assignee)}
                        options={[
                            { label: intl.formatMessage({ id: 'mail.filter.mine' }), value: 'me' },
                            { label: intl.formatMessage({ id: 'mail.filter.unassigned' }), value: 'unassigned' },
                            { label: intl.formatMessage({ id: 'mail.filter.followUp' }), value: 'follow-up' },
                            { label: intl.formatMessage({ id: 'mail.filter.archived' }), value: 'archived' },
                            { label: intl.formatMessage({ id: 'mail.filter.all' }), value: 'all' },
                        ]}
                    />
                    <Input
                        allowClear
                        size="small"
                        prefix={<SearchOutlined />}
                        placeholder={intl.formatMessage({ id: 'mail.searchPlaceholder' })}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: 220 }}
                    />
                    <Space size={6}>
                        <Switch size="small" checked={hideLinked} onChange={setHideLinked} />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <FormattedMessage id="mail.hideAccountMail" defaultMessage="Hide account mail" />
                        </Text>
                    </Space>
                </Space>

                <Space>
                    {/* Local time: this is read at a glance to answer "is what
                        I am looking at current?", and UTC would make that a
                        mental subtraction. */}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {syncStatus?.lastSyncAt
                            ? intl.formatMessage(
                                  { id: 'mail.lastSynced' },
                                  { when: new Date(syncStatus.lastSyncAt).toLocaleString() },
                              )
                            : intl.formatMessage({ id: 'mail.neverSynced' })}
                        {syncStatus && !syncStatus.enabled &&
                            ` · ${intl.formatMessage({ id: 'mail.autoSyncOff' })}`}
                    </Text>
                    <Button
                        icon={<CloudDownloadOutlined />}
                        loading={runSyncResult.isLoading}
                        onClick={syncNow}
                    >
                        <FormattedMessage id="mail.syncNow" defaultMessage="Sync now" />
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching} />
                    <Button loading={backfillResult.isLoading} onClick={runBackfill}>
                        <FormattedMessage id="mail.backfill" defaultMessage="Route older mail" />
                    </Button>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => { setReplying(false); setReplyAll(false); setComposerOpen(true); }}
                    >
                        <FormattedMessage id="mail.compose" defaultMessage="Compose" />
                    </Button>
                </Space>
            </Space>

            <Row gutter={16}>
                <Col xs={24} lg={9}>
                    <Card
                        size="small"
                        title={
                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                {/* Inline count rather than an overlay Badge: the
                                    card header clips anything positioned outside
                                    the title's own box, which cut the number in half. */}
                                <Space size={6}>
                                    <span><FormattedMessage id="mail.threads" defaultMessage="Conversations" /></span>
                                    {threads.filter((t) => t.unread).length > 0 && (
                                        <Tag color="red" style={{ marginInlineEnd: 0 }}>
                                            {threads.filter((t) => t.unread).length}
                                        </Tag>
                                    )}
                                </Space>
                                {threads.some((t) => t.unread) && (
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<CheckOutlined />}
                                        loading={markAllReadResult.isLoading}
                                        onClick={() => markAllRead({ jwtToken })}
                                    >
                                        <FormattedMessage id="mail.markAllRead" defaultMessage="Mark all read" />
                                    </Button>
                                )}
                            </Space>
                        }
                        styles={{ body: { padding: 0, maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' } }}
                    >
                        {threads.length === 0 ? (
                            <Empty
                                style={{ padding: 24 }}
                                description={intl.formatMessage({ id: 'mail.noThreads' })}
                            />
                        ) : (
                            <List
                                dataSource={threads}
                                renderItem={(thread) => (
                                    <ThreadRow
                                        thread={thread}
                                        selected={thread.threadKey === selectedThreadKey}
                                        onSelect={() => openThread(thread.threadKey)}
                                        onToggleFollowUp={() =>
                                            setFollowUp({
                                                jwtToken,
                                                threadKey: thread.threadKey,
                                                followUp: !thread.followUp,
                                            })
                                        }
                                        onToggleArchived={() => toggleArchived(thread)}
                                    />
                                )}
                            />
                        )}
                    </Card>
                </Col>

                <Col xs={24} lg={15}>
                    <MailThreadView
                        threadKey={selectedThreadKey}
                        onReply={(all) => { setReplying(true); setReplyAll(all); setComposerOpen(true); }}
                    />
                </Col>
            </Row>

            <MailComposer
                open={composerOpen}
                onClose={() => setComposerOpen(false)}
                thread={replying ? selectedThread : null}
                replyAll={replyAll}
                onSent={(threadKey) => setSelectedThreadKey(threadKey)}
            />
        </div>
    );
};

export default AdminMailPage;
